import React, { useState, ReactNode } from "react";
import { Col, Row } from "../../components/Grid";
import classnames from "classnames";
import styles from "./FilterAside.module.scss";
import { FilterCounts, FilterType, ListingConfig } from "../Listing/filters";
import GetText, { useString } from "../../i18n/GetText";
import { FilterSelectionState } from "../Listing/useFilterSelectionState";
import { SanityRawContent } from "../../model/common";
import IsDesktop from "../../components/IsDesktop/IsDesktop";
import BlockContent from "../../components/BlockContent/BlockContent";
import Search from "../../components/Inputs/Search/Search";
import IsMobile from "../../components/IsMobile/IsMobile";
import Text from "../Text/Text";
import PlainButton from "../../components/Button/PlainButton/PlainButton";
import Divider from "../../components/Divider/Divider";
import Button from "../../components/Button/StandardButton/Button";
import FilterIcon from "../../icons/FilterIcon";

interface FilterAsideProps {
  config: ListingConfig<any>;
  filtersByConfigId: Record<string, FilterType[]>;
  filterCounts: FilterCounts;
  filterState: FilterSelectionState;
  title: ReactNode;
  sideBarText?: SanityRawContent;
  query: string;
  setQuery: (query: string) => void;
}

const FilterAside = ({
  config,
  title,
  sideBarText,
  filterState,
  filterCounts,
  filtersByConfigId,
  query,
  setQuery
}: FilterAsideProps) => {
  const { isSelected, setSelected } = filterState;
  const [displayFilters, setDisplayFilters] = useState<boolean>(false);
  const filtersButtonAriaLabel = useString(
    displayFilters ? "hide-filters" : "show-filters"
  );
  const toggleFilters = () => {
    setDisplayFilters(!displayFilters);
  };

  const AlwaysOnDesktopButOnlyIfDisplayedOnMobile = displayFilters
    ? React.Fragment
    : IsDesktop;

  return (
    <div className={styles.leftAside}>
      <Text variant="h1" className={styles.title}>
        {title}
      </Text>
      {sideBarText && <BlockContent content={sideBarText} />}
      <Row>
        <Col xs={12}>
          <div className={`${styles.searchContainer}`}>
            <Search
              className={styles.search}
              onChange={e => setQuery(e.currentTarget.value)}
              value={query}
            />
            <IsMobile>
              <PlainButton
                onClick={toggleFilters}
                aria-label={filtersButtonAriaLabel}
              >
                <FilterIcon
                  height={20}
                  width={20}
                  className={classnames(
                    styles.filterIcon,
                    displayFilters ? styles.strokeAccent : styles.strokeDefault
                  )}
                />
              </PlainButton>
            </IsMobile>
          </div>
        </Col>
      </Row>
      <AlwaysOnDesktopButOnlyIfDisplayedOnMobile>
        <Row>
          <Col xs={12}>
            <div className={styles.filterContainer}>
              {config.filters.map((c, index) => {
                const Wrapper =
                  c.location === "top" ? IsMobile : React.Fragment;
                return (
                  <Wrapper key={c.id}>
                    {c.component({
                      filterCounts,
                      isSelected,
                      setSelected,
                      titleId: c.titleId,
                      inputNamePrefix: "input",
                      filterData: filtersByConfigId[c.id]
                    })}
                    {index < config.filters.length - 1 && <Divider />}
                  </Wrapper>
                );
              })}
              <IsMobile>
                <div className="row center-xs">
                  <Button
                    primary={true}
                    onClick={toggleFilters}
                    style={{ margin: 10 }}
                  >
                    <GetText id={"close"} />
                  </Button>
                </div>
              </IsMobile>
            </div>
          </Col>
        </Row>
      </AlwaysOnDesktopButOnlyIfDisplayedOnMobile>
    </div>
  );
};

export default FilterAside;
